// src/component/FirestoreRulesProbe.js
// JSXあり / JS（TSなし）
// 依存: react, @mui/material, firebase v9
//
// 目的:
// - 現在の Firestore Rules が「catch-all に負けてないか」をローカルで検査する
// - 特に users / mmPosts の "本来NGの操作" が通ってしまうかをチェック
//
// 使い方:
// - ルーティングして /admin/rules-probe 等で開く（管理者専用にしない方が検査しやすい）
// - 非管理者ユーザーでログインして実行 → NG操作が通ったら危険（catch-all勝ち）
// - 管理者ユーザーでログインして実行 → adminのみ許可の項目が通るか確認も可能
//
// 注意:
// - 書き込みを伴うテストがある（mmPosts作成/更新）
// - users の create/update は "他人UID" を指定しても実際に作れないことが多いが、
//   get/update が通るかが本質（他人のuserDocが存在するとより確実に判定できる）

import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Paper,
  Button,
  TextField,
  Divider,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";

import { auth, db } from "../api/firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  addDoc,
  collection,
  deleteDoc,
} from "firebase/firestore";

const isDenied = (e) => {
  const code = e?.code || "";
  return (
    code === "permission-denied" ||
    code.includes("permission-denied") ||
    String(e?.message || "").toLowerCase().includes("permission")
  );
};

const fmtErr = (e) => {
  const code = e?.code ? ` (${e.code})` : "";
  return `${e?.message || String(e)}${code}`;
};

const ResultRow = ({ title, result }) => {
  if (!result) return null;

  const ok = result.status === "ok";
  const denied = result.status === "denied";
  const warn = result.status === "warn";

  return (
    <Box sx={{ py: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography sx={{ fontWeight: 700, minWidth: 220 }}>{title}</Typography>
        <Chip
          size="small"
          label={ok ? "OK" : denied ? "DENIED" : "WARN"}
          color={ok ? "success" : denied ? "error" : "warning"}
        />
      </Stack>
      {result.message && (
        <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.85 }}>
          {result.message}
        </Typography>
      )}
    </Box>
  );
};

export default function FirestoreRulesProbe() {
  const [user, setUser] = useState(() => auth.currentUser);
  const [loadingAuth, setLoadingAuth] = useState(!auth.currentUser);

  const [myIsAdmin, setMyIsAdmin] = useState(false);
  const [loadingMyAdmin, setLoadingMyAdmin] = useState(false);

  // 「他人UID」：ここに実在する user doc の uid を入れると検査精度が上がる
  const [otherUid, setOtherUid] = useState("");

  // mmPosts テスト対象：他人の投稿ID（存在するmmPosts doc id）
  const [otherPostId, setOtherPostId] = useState("");

  // 実行結果
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState({});

  // テストで作った自分の投稿ID
  const [myCreatedPostId, setMyCreatedPostId] = useState("");

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      setUser(u);
      setLoadingAuth(false);
    });
    return () => unsub();
  }, []);

  // 自分の isAdmin を users/{uid} から読む（読めなければそれ自体が問題のヒント）
  useEffect(() => {
    const run = async () => {
      if (!user?.uid) return;
      setLoadingMyAdmin(true);
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        const v = snap.exists() ? Boolean(snap.data()?.isAdmin) : false;
        setMyIsAdmin(v);
      } catch (e) {
        // 読めない場合は admin判定不能だが、結果に出す
        setMyIsAdmin(false);
      } finally {
        setLoadingMyAdmin(false);
      }
    };
    run();
  }, [user?.uid]);

  const headline = useMemo(() => {
    if (loadingAuth) return "認証状態を確認中…";
    if (!user) return "未ログインです（ログインしてから実行してください）";
    return `ログイン中: ${user.uid}`;
  }, [loadingAuth, user]);

  // --- users テスト ---
  const testUsersAccess = async () => {
    const out = {};

    // 1) 自分の users doc get
    try {
      const s = await getDoc(doc(db, "users", user.uid));
      out.myUserGet = {
        status: "ok",
        message: `自分の users/${user.uid} get: success (exists=${s.exists()})`,
      };
    } catch (e) {
      out.myUserGet = {
        status: isDenied(e) ? "denied" : "warn",
        message: `自分の users/${user.uid} get: ${fmtErr(e)}`,
      };
    }

    // 2) 他人の users doc get（存在する他人UIDだと判定が明確）
    if (!otherUid.trim()) {
      out.otherUserGet = {
        status: "warn",
        message: "他人UIDが未入力のためスキップ（存在するUIDを入れると判定が明確）",
      };
    } else {
      try {
        const s = await getDoc(doc(db, "users", otherUid.trim()));
        // ★ここが重要：非管理者で success なら危険（本人限定が壊れてる）
        out.otherUserGet = {
          status: "ok",
          message: `他人の users/${otherUid.trim()} get: success (exists=${s.exists()})`,
        };
      } catch (e) {
        out.otherUserGet = {
          status: isDenied(e) ? "denied" : "warn",
          message: `他人の users/${otherUid.trim()} get: ${fmtErr(e)}`,
        };
      }
    }

    // 3) 他人の users doc update（存在する他人UIDだと判定が明確）
    if (!otherUid.trim()) {
      out.otherUserUpdate = {
        status: "warn",
        message: "他人UIDが未入力のためスキップ",
      };
    } else {
      try {
        await updateDoc(doc(db, "users", otherUid.trim()), {
          __probeTouchedAt: serverTimestamp(),
          __probeTouchedBy: user.uid,
        });
        out.otherUserUpdate = {
          status: "ok",
          message:
            "他人usersの update が成功。これは危険（本人限定が壊れている/または管理者権限）。",
        };
      } catch (e) {
        out.otherUserUpdate = {
          status: isDenied(e) ? "denied" : "warn",
          message: `他人usersの update: ${fmtErr(e)}`,
        };
      }
    }

    return out;
  };

  // --- mmPosts テスト ---
  const testMmPostsAccess = async () => {
    const out = {};
    let createdId = "";

    // 1) 自分の投稿を作る（許可されてるはず: create & userId==uid）
    try {
      const ref = await addDoc(collection(db, "mmPosts"), {
        userId: user.uid,
        body: `__probe post by ${user.uid}`,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        likeCount: 0,
        commentCount: 0,
      });
      createdId = ref.id;
      setMyCreatedPostId(ref.id);
      out.createOwnPost = {
        status: "ok",
        message: `自分の mmPosts create success: ${ref.id}`,
      };
    } catch (e) {
      out.createOwnPost = {
        status: isDenied(e) ? "denied" : "warn",
        message: `自分の mmPosts create: ${fmtErr(e)}`,
      };
    }

    // 2) 自分の投稿を「本文更新」する（投稿者本人なので通常OKのはず）
    if (!createdId) {
      out.updateOwnBody = {
        status: "warn",
        message: "自分の投稿が作れなかったのでスキップ",
      };
    } else {
      try {
        await updateDoc(doc(db, "mmPosts", createdId), {
          body: "__probe updated body (should be allowed for owner)",
          updatedAt: serverTimestamp(),
        });
        out.updateOwnBody = {
          status: "ok",
          message: "自分の投稿本文 update success（期待どおり）",
        };
      } catch (e) {
        out.updateOwnBody = {
          status: isDenied(e) ? "denied" : "warn",
          message: `自分の投稿本文 update: ${fmtErr(e)}`,
        };
      }
    }

    // 3) 他人の投稿を「本文更新」する（本来DENYされるべき）
    if (!otherPostId.trim()) {
      out.updateOthersBody = {
        status: "warn",
        message:
          "他人の投稿IDが未入力のためスキップ（他人mmPostsのdocIdを入れると検査できます）",
      };
    } else {
      try {
        await updateDoc(doc(db, "mmPosts", otherPostId.trim()), {
          body: "__probe malicious body change",
          updatedAt: serverTimestamp(),
        });
        out.updateOthersBody = {
          status: "ok",
          message:
            "他人の投稿本文 update が成功。これは危険（mmPostsの制限がcatch-allに負けている可能性大）。",
        };
      } catch (e) {
        out.updateOthersBody = {
          status: isDenied(e) ? "denied" : "warn",
          message: `他人の投稿本文 update: ${fmtErr(e)}`,
        };
      }
    }

    // 4) 他人の投稿を「likeCount等だけ更新」する（あなたの意図では許可されうる）
    if (!otherPostId.trim()) {
      out.updateOthersCountsOnly = {
        status: "warn",
        message: "他人の投稿IDが未入力のためスキップ",
      };
    } else {
      try {
        await updateDoc(doc(db, "mmPosts", otherPostId.trim()), {
          likeCount: 999, // 実験値
          updatedAt: serverTimestamp(),
        });
        out.updateOthersCountsOnly = {
          status: "ok",
          message:
            "他人投稿の likeCount 更新が成功。意図どおりか、またはcatch-all勝ちかは次の「本文更新」の結果とセットで判断。",
        };
      } catch (e) {
        out.updateOthersCountsOnly = {
          status: isDenied(e) ? "denied" : "warn",
          message: `他人投稿の likeCount 更新: ${fmtErr(e)}`,
        };
      }
    }

    return out;
  };

  // （任意）後片付け：自分が作ったprobe投稿を削除
  const cleanupMyPost = async () => {
    if (!myCreatedPostId) return;
    try {
      await deleteDoc(doc(db, "mmPosts", myCreatedPostId));
      setResults((p) => ({
        ...p,
        cleanup: { status: "ok", message: `cleanup: deleted ${myCreatedPostId}` },
      }));
      setMyCreatedPostId("");
    } catch (e) {
      setResults((p) => ({
        ...p,
        cleanup: { status: "warn", message: `cleanup failed: ${fmtErr(e)}` },
      }));
    }
  };

  const runAll = async () => {
    if (!user?.uid) return;
    setRunning(true);
    setResults({});
    try {
      const r1 = await testUsersAccess();
      const r2 = await testMmPostsAccess();

      setResults({
        ...r1,
        ...r2,
      });
    } finally {
      setRunning(false);
    }
  };

  const riskSummary = useMemo(() => {
    // 判定ロジック：非管理者で他人users get/update が ok、他人mmPosts本文更新が ok なら危険
    const otherUserGetOk = results?.otherUserGet?.status === "ok";
    const otherUserUpdateOk = results?.otherUserUpdate?.status === "ok";
    const othersBodyOk = results?.updateOthersBody?.status === "ok";

    const maybeRisk = otherUserGetOk || otherUserUpdateOk || othersBodyOk;

    if (!Object.keys(results || {}).length) return null;

    if (maybeRisk) {
      return {
        severity: "error",
        text:
          "危険: 本来DENYすべき操作が成功しています。catch-all が個別制限に勝っている可能性が高いです。",
      };
    }
    return {
      severity: "success",
      text:
        "概ね安全: 本来DENYすべき操作がDENIEDになっています（ただしスキップ項目がある場合は入力して再検査推奨）。",
    };
  }, [results]);

  return (
    <Box sx={{ p: 2.5, maxWidth: 960, mx: "auto" }}>
      <Paper sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack spacing={1.2}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Firestore Rules Probe（users / mmPosts）
          </Typography>

          <Typography variant="body2" sx={{ opacity: 0.85 }}>
            {headline}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            {loadingMyAdmin ? (
              <CircularProgress size={18} />
            ) : (
              <Chip
                size="small"
                label={myIsAdmin ? "自分は admin=true" : "自分は admin=false（推定）"}
                color={myIsAdmin ? "success" : "default"}
              />
            )}
<Typography variant="caption" sx={{ opacity: 0.7 }}>
  ※ users/{`{uid}`} が読めない場合は admin 推定が外れます
</Typography>

          </Stack>

          <Divider sx={{ my: 1.5 }} />

          <Alert severity="info">
            非管理者でログインして実行してください。ここで「他人users get/update」や「他人mmPosts本文update」が成功したら、
            ほぼ確実に catch-all が勝っています。
          </Alert>

          <Stack spacing={1.2} sx={{ mt: 1 }}>
            <TextField
              label="他人のUID（users/{otherUid} が存在するもの推奨）"
              value={otherUid}
              onChange={(e) => setOtherUid(e.target.value)}
              placeholder="例: abcdefg123..."
              fullWidth
            />
            <TextField
              label="他人の mmPosts docId（存在する投稿ID）"
              value={otherPostId}
              onChange={(e) => setOtherPostId(e.target.value)}
              placeholder="例: QxYz...."
              fullWidth
            />
          </Stack>

          <Stack direction="row" spacing={1.2} sx={{ mt: 1 }}>
            <Button
              variant="contained"
              onClick={runAll}
              disabled={!user?.uid || running}
            >
              {running ? "検査中..." : "検査を実行"}
            </Button>

            <Button
              variant="outlined"
              onClick={cleanupMyPost}
              disabled={!myCreatedPostId || running}
            >
              自分のprobe投稿を削除
            </Button>
          </Stack>

          {riskSummary && (
            <Alert severity={riskSummary.severity} sx={{ mt: 1.5 }}>
              {riskSummary.text}
            </Alert>
          )}

          <Divider sx={{ my: 1.5 }} />

          <Typography sx={{ fontWeight: 800, mt: 0.5 }}>
            結果
          </Typography>

          <ResultRow title="users: 自分doc get" result={results.myUserGet} />
          <ResultRow title="users: 他人doc get" result={results.otherUserGet} />
          <ResultRow
            title="users: 他人doc update"
            result={results.otherUserUpdate}
          />

          <Divider sx={{ my: 1.2 }} />

          <ResultRow
            title="mmPosts: 自分投稿 create"
            result={results.createOwnPost}
          />
          <ResultRow
            title="mmPosts: 自分投稿 本文update"
            result={results.updateOwnBody}
          />
          <ResultRow
            title="mmPosts: 他人投稿 本文update（本来DENY）"
            result={results.updateOthersBody}
          />
          <ResultRow
            title="mmPosts: 他人投稿 likeCount更新（意図では許可候補）"
            result={results.updateOthersCountsOnly}
          />

          <ResultRow title="cleanup" result={results.cleanup} />

          <Divider sx={{ my: 1.2 }} />

          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            判断基準:
            <br />
            ・非管理者で「users: 他人doc get/update」が OK → 危険
            <br />
            ・非管理者で「mmPosts: 他人投稿 本文update」が OK → 危険（catch-all勝ち）
            <br />
            ・「他人投稿 likeCount更新」が OK でも、本文update が DENIED なら意図どおりの可能性が高い
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}
